import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button, List, Modal, Input, Checkbox, Space, Card, Typography, Popconfirm } from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from "@ant-design/icons";
import { API_URL, getCredentials } from "../shared/utils/Util";
import { getFetchT, fetchT, dollarToCents, centsToDollar } from "../shared/utils/TypedUtil";
import { useNotification } from "../shared/hooks";
import theme from "../config/theme";

const { Title, Text } = Typography;

interface ItemModificationChoice {
  id?: string;
  item_modification_schema_id?: string;
  description: string;
  price_adjustment: number;
  seq: number;
  default_selected: boolean;
}

interface ItemModificationSchema {
  id: string;
  restaurant_id: string;
  description: string;
  allow_multiple_choice: boolean;
  optional: boolean;
  choices: ItemModificationChoice[];
}

const emptyFormState: Partial<ItemModificationSchema> = {
  description: "",
  allow_multiple_choice: false,
  optional: false,
  choices: [],
};

const ItemModificationSchemaEditorPage: React.FC = () => {
  const { t } = useTranslation();
  const { createSuccess, updateSuccess, deleteSuccess, notifyError } = useNotification();
  const [schemas, setSchemas] = useState<ItemModificationSchema[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingSchema, setEditingSchema] = useState<ItemModificationSchema | null>(null);
  const [formState, setFormState] = useState<Partial<ItemModificationSchema>>(emptyFormState);
  const [isFormValid, setIsFormValid] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  const restaurant_id = sessionStorage.getItem("restaurant_id");

  const validateForm = () => {
    if (!formState.description || formState.description.trim() === "") {
      return false;
    }
    if (!formState.choices || formState.choices.length === 0) {
      return false;
    }
    for (const choice of formState.choices) {
      if (!choice.description || choice.description.trim() === "") {
        return false;
      }
    }
    return true;
  };

  useEffect(() => {
    setIsFormValid(validateForm());
  }, [formState]);

  const fetchSchemas = () => {
    if (restaurant_id) {
      getFetchT<{ schemas: ItemModificationSchema[] }>(
        `${API_URL}/v1/r/${restaurant_id}/item_modification_schemas`,
        (data) => {
          const sorted = (data.schemas ?? []).sort((a, b) =>
            a.description.localeCompare(b.description)
          );
          setSchemas(sorted);
        },
        (err) => {
          notifyError(t("error"), err);
        }
      );
    }
  };

  useEffect(() => {
    fetchSchemas();
  }, [restaurant_id]);

  const resetAndCloseModal = () => {
    setIsModalVisible(false);
    setEditingSchema(null);
    setFormState(emptyFormState);
  };

  const handleEdit = (schema: ItemModificationSchema) => {
    setEditingSchema(schema);
    setFormState({
      ...schema,
      choices: schema.choices.map((c) => ({
        ...c,
        price_adjustment: parseFloat(centsToDollar(c.price_adjustment)),
      })),
    });
    setIsModalVisible(true);
  };

  const handleOk = () => {
    const choicesInCents = (formState.choices || []).map((c, i) => ({
      ...c,
      price_adjustment: dollarToCents(c.price_adjustment),
      seq: i + 1,
    }));

    if (editingSchema) {
      // Update existing schema (PUT, no wrapper)
      fetchT(
        "PUT",
        `${API_URL}/v1/r/${restaurant_id}/item_modification_schemas/${editingSchema.id}`,
        { ...formState, choices: choicesInCents },
        () => {
          resetAndCloseModal();
          fetchSchemas();
          updateSuccess(t("edit_customization_options"));
        },
        (err) => {
          notifyError(t("error"), err);
        }
      );
    } else {
      // Create new schema (POST, with wrapper)
      fetchT(
        "POST",
        `${API_URL}/v1/r/${restaurant_id}/item_modification_schemas`,
        { item_modification_schema: { ...formState, choices: choicesInCents } },
        () => {
          resetAndCloseModal();
          fetchSchemas();
          createSuccess(t("edit_customization_options"));
        },
        (err) => {
          notifyError(t("error"), err);
        }
      );
    }
  };

  const handleAddChoice = () => {
    setFormState((prev) => ({
      ...prev,
      choices: [
        ...(prev.choices || []),
        {
          description: "",
          price_adjustment: 0,
          seq: (prev.choices?.length || 0) + 1,
          default_selected: false,
        },
      ],
    }));
  };

  const handleRemoveChoice = (index: number) => {
    setFormState((prev) => ({
      ...prev,
      choices: prev.choices?.filter((_, i) => i !== index),
    }));
  };

  const handleMoveChoice = (index: number, direction: "up" | "down") => {
    setFormState((prev) => {
      const choices = [...(prev.choices || [])];
      const targetIndex = direction === "up" ? index - 1 : index + 1;
      [choices[index], choices[targetIndex]] = [choices[targetIndex], choices[index]];
      return { ...prev, choices };
    });
  };

  const handleChoiceChange = (
    index: number,
    field: "description" | "price_adjustment" | "seq" | "default_selected",
    value: string | number | boolean
  ) => {
    setFormState((prev) => {
      const newChoices = [...(prev.choices || [])];
      newChoices[index] = {
        ...newChoices[index],
        [field]: value,
      };
      // When single-choice mode, deselect other defaults
      if (field === "default_selected" && value && !prev.allow_multiple_choice) {
        for (let i = 0; i < newChoices.length; i++) {
          if (i !== index) {
            newChoices[i] = { ...newChoices[i], default_selected: false };
          }
        }
      }
      return { ...prev, choices: newChoices };
    });
  };

  return (
    <>
      <Card
        style={{
          borderRadius: theme.borderRadius.xl,
          boxShadow: theme.shadows.base,
        }}
        styles={{ body: { padding: theme.spacing.xl } }}
      >
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingSchema(null);
            setFormState(emptyFormState);
            setIsModalVisible(true);
          }}
          style={{
            marginBottom: theme.spacing.lg,
            borderRadius: theme.borderRadius.lg,
            height: theme.layout.button.height,
          }}
        >
          {t("add_customization_option")}
        </Button>
        <List
          bordered
          dataSource={schemas}
          style={{
            borderRadius: theme.borderRadius.lg,
            overflow: "hidden",
          }}
          renderItem={(schema) => (
            <List.Item
              style={{
                padding: theme.spacing.lg,
                transition: theme.transitions.fast,
              }}
              className="theme-fade-in"
              actions={[
                <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(schema)}>
                  {t("edit")}
                </Button>,
              ]}
            >
              <List.Item.Meta
                title={
                  <Text strong style={{ fontSize: theme.typography.fontSize.lg }}>
                    {schema.description}
                  </Text>
                }
                description={
                  <div style={{ marginTop: theme.spacing.sm }}>
                    <Text type="secondary">
                      {(schema.choices ?? []).map((c) => c.description).join(", ")}
                    </Text>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      </Card>
      <Modal
        title={
          <Title level={4} style={{ margin: 0 }}>
            {editingSchema ? t("edit_customization_option") : t("add_customization_option")}
          </Title>
        }
        open={isModalVisible}
        onOk={handleOk}
        onCancel={resetAndCloseModal}
        okText={editingSchema ? t("save") : t("add")}
        cancelText={t("cancel")}
        width={800}
        destroyOnHidden
        okButtonProps={{
          disabled: !isFormValid,
          style: {
            borderRadius: theme.borderRadius.lg,
            height: theme.layout.button.height,
          },
        }}
        cancelButtonProps={{
          style: {
            borderRadius: theme.borderRadius.lg,
            height: theme.layout.button.height,
          },
        }}
        styles={{
          body: { padding: theme.spacing.xl },
        }}
      >
        <Space direction="vertical" style={{ width: "100%", gap: theme.spacing.md }}>
          <div>
            <Text strong>{t("option_name")} *</Text>
            <Input
              placeholder={t("please_enter_option_name")}
              value={formState.description}
              onChange={(e) => setFormState({ ...formState, description: e.target.value })}
              style={{
                marginTop: theme.spacing.xs,
                borderRadius: theme.borderRadius.lg,
                height: theme.layout.button.height,
              }}
              required
              status={!formState.description ? "error" : ""}
            />
          </div>

          <div style={{ marginTop: theme.spacing.md }}>
            <Checkbox
              checked={formState.allow_multiple_choice}
              onChange={(e) => {
                const checked = e.target.checked;
                let choices = formState.choices;
                // Switching to single-choice: keep only the first default
                if (!checked && choices) {
                  let foundFirst = false;
                  choices = choices.map((c) => {
                    if (c.default_selected && !foundFirst) {
                      foundFirst = true;
                      return c;
                    }
                    return c.default_selected ? { ...c, default_selected: false } : c;
                  });
                }
                setFormState({
                  ...formState,
                  allow_multiple_choice: checked,
                  choices,
                });
              }}
              style={{ marginRight: theme.spacing.lg }}
            >
              {t("allow_multiple_selection")}
            </Checkbox>
            <Checkbox
              checked={formState.optional}
              onChange={(e) => setFormState({ ...formState, optional: e.target.checked })}
            >
              {t("optional")}
            </Checkbox>
          </div>

          <div style={{ marginTop: theme.spacing.lg }}>
            <Text strong>{t("option_list")} *</Text>
            <div style={{ marginTop: theme.spacing.md }}>
              {formState.choices?.map((choice, index) => (
                <Space
                  key={index}
                  style={{
                    display: "flex",
                    marginBottom: theme.spacing.md,
                    padding: theme.spacing.md,
                    background: theme.colors.neutral.gray50,
                    borderRadius: theme.borderRadius.lg,
                  }}
                >
                  <Input
                    placeholder={t("option_name")}
                    value={choice.description}
                    onChange={(e) => handleChoiceChange(index, "description", e.target.value)}
                    required
                    status={!choice.description ? "error" : ""}
                    style={{
                      borderRadius: theme.borderRadius.lg,
                      height: theme.layout.button.height,
                    }}
                  />
                  <Input
                    placeholder={t("price_adjustment")}
                    addonBefore="$"
                    type="number"
                    value={choice.price_adjustment}
                    onChange={(e) => {
                      handleChoiceChange(
                        index,
                        "price_adjustment",
                        parseFloat(e.target.value) || 0
                      );
                    }}
                    style={{
                      borderRadius: theme.borderRadius.lg,
                      height: theme.layout.button.height,
                      minWidth: "120px",
                      maxWidth: theme.cardSizes.minWidth.sm,
                    }}
                  />
                  <Checkbox
                    checked={choice.default_selected}
                    onChange={(e) =>
                      handleChoiceChange(index, "default_selected", e.target.checked)
                    }
                  >
                    {t("default")}
                  </Checkbox>
                  <Button
                    type="text"
                    icon={<ArrowUpOutlined />}
                    disabled={index === 0}
                    onClick={() => handleMoveChoice(index, "up")}
                    style={{
                      borderRadius: theme.borderRadius.lg,
                      height: theme.layout.button.height,
                    }}
                  />
                  <Button
                    type="text"
                    icon={<ArrowDownOutlined />}
                    disabled={index === (formState.choices?.length ?? 1) - 1}
                    onClick={() => handleMoveChoice(index, "down")}
                    style={{
                      borderRadius: theme.borderRadius.lg,
                      height: theme.layout.button.height,
                    }}
                  />
                  <Button
                    type="primary"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveChoice(index)}
                    style={{
                      borderRadius: theme.borderRadius.lg,
                      height: theme.layout.button.height,
                    }}
                  />
                </Space>
              ))}
              <Button
                type="dashed"
                onClick={handleAddChoice}
                icon={<PlusOutlined />}
                style={{
                  width: "100%",
                  borderRadius: theme.borderRadius.lg,
                  height: theme.layout.button.height,
                  marginTop: theme.spacing.sm,
                }}
              >
                {t("add_option")}
              </Button>
            </div>
          </div>
        </Space>
      </Modal>
    </>
  );
};

export default ItemModificationSchemaEditorPage;
